// Headless-only dispatcher for the Open-ended family (pptx keys oe/cx/scx/cn).
// Legacy getItemOeParams rasterizes exactly two things: the wordcloud SVG
// (viewMode 'wordcloud' or chartMode 'wc') and the sunburst/treemap HC chart
// (getItemChart). Every other viewMode (verbatim/sentiments/tags/chart) is
// metadata-only — PPTX renders those natively from data — so this dispatcher
// emits an explicit error for them instead of hanging the driver.
//
// The dispatcher carries NO rasterization of its own: it mounts the live
// sub-chart, whose existing useExportableChart (wc Chart — P4; ChartContainer
// inside ComboChart — P2) owns the export contract. The only hook here is the
// inert error-emitter for unsupported viewModes.
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/tasks/10-oe.md
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { useExportableChart } from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { calcWords } from '@aytm/app/stats/page/question-views/cx/lib/scope'

import ComboChart from '@aytm/app/stats/page/question-views/cx/components/chart/Chart'
import HeadlessWordCloud from '@aytm/app/stats/page/question-views/components/word-cloud/HeadlessWordCloud'
import { isWcChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const PNG_VIEW_MODES = ['wordcloud', 'sunburst', 'treemap']

const noop = () => {}

const OeDispatcher = ({
  qType,
  questionKey,
  id,
  answer,
  answerVariants,
  levels,
  viewMode,
  chartMode,
  scope,
  wcConfig,
  format,
  colorTheme,
  chartLegendMode,
  features,
  width,
  height
}) => {
  const effectiveViewMode = isWcChartMode(chartMode) ? 'wordcloud' : viewMode
  const supported = PNG_VIEW_MODES.includes(effectiveViewMode)

  useExportableChart({
    deps: [!supported],
    prepareForExport: async () => {
      throw new Error(`oe viewMode '${effectiveViewMode}' is metadata-only in legacy PPTX (no rasterization)`)
    }
  })

  if (!supported) return null

  if (effectiveViewMode === 'wordcloud') {
    // oe questions carry the processed-corpus cloudWords object on the answer;
    // combo questions (nested answerVariants) derive words from AV amounts.
    const isCombo = Array.isArray(answerVariants) && answerVariants.length > 0
    const rawData = isCombo
      ? { words: calcWords(levels, scope, answer, answerVariants) }
      : answer?.cloudWords || { words: [] }
    // Combo mirror of the live @rejects: the family fixes maxWords at 300, and cx word
    // triples carry av.position in the stop-level slot, so its stop filters must be off
    // or a1/a2 words vanish.
    const comboRejects = { maxWords: 300, ...(qType === 'cx' && { stop1: 0, stop2: 0, stop3: 0, stop4: 0, stop5: 0 }) }
    const rejects = ['cx', 'scx'].includes(qType) ? comboRejects : {}

    return (
      <HeadlessWordCloud
        id={id}
        questionKey={questionKey}
        rawData={rawData}
        wcConfig={wcConfig}
        rejects={rejects}
        width={width}
        height={height}
        useRawData={format?.useRawData || false}
      />
    )
  }

  return (
    <ComboChart
      questionKey={questionKey}
      qType={qType}
      viewMode={effectiveViewMode}
      answerVariants={answerVariants}
      answer={answer}
      scope={scope}
      onScopeChange={noop}
      format={format}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      features={features}
    />
  )
}

OeDispatcher.propTypes = {
  qType: PropTypes.string.isRequired,
  questionKey: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  answer: answerPropType,
  answerVariants: PropTypes.array,
  levels: PropTypes.number,
  viewMode: PropTypes.string.isRequired,
  chartMode: PropTypes.string,
  scope: PropTypes.array,
  wcConfig: PropTypes.object,
  format: PropTypes.object,
  colorTheme: PropTypes.array,
  chartLegendMode: PropTypes.string,
  features: PropTypes.object,
  width: PropTypes.number,
  height: PropTypes.number
}

OeDispatcher.defaultProps = {
  scope: [],
  wcConfig: {},
  format: {},
  features: {}
}

OeDispatcher.displayName = 'OeDispatcher'

export default OeDispatcher
